import React, { useEffect } from 'react';
import { Download, Eye, FileText, X } from 'lucide-react';

function isImageUrl(url: string, name?: string | null): boolean {
  const lower = `${url} ${name ?? ''}`.toLowerCase();
  return /\.(jpe?g|png|webp|gif|bmp)(\?|$)/i.test(lower) || lower.includes('image/');
}

function isPdfUrl(url: string, name?: string | null): boolean {
  const lower = `${url} ${name ?? ''}`.toLowerCase();
  return /\.pdf(\?|$)/i.test(lower) || lower.includes('application/pdf');
}

export interface AttachmentPreviewModalProps {
  show: boolean;
  url: string | null;
  fileName?: string | null;
  onClose: () => void;
}

const AttachmentPreviewModal: React.FC<AttachmentPreviewModalProps> = ({
  show,
  url,
  fileName,
  onClose,
}) => {
  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [show, onClose]);

  if (!show || !url) return null;

  const image = isImageUrl(url, fileName);
  const pdf = isPdfUrl(url, fileName);

  return (
    <div
      className="fixed inset-0 z-[150] flex flex-col bg-black/80"
      dir="rtl"
      onClick={onClose}
    >
      <div
        className="flex items-center justify-between gap-3 px-4 py-3 bg-black/50 text-white shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Eye size={18} className="shrink-0 text-sky-300" />
          <span className="font-medium truncate">{fileName || 'پیش‌نمایش پیوست'}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a
            href={url}
            download={fileName || true}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <Download size={16} />
            دانلود
          </a>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" aria-label="بستن">
            <X size={20} />
          </button>
        </div>
      </div>

      <div
        className="flex-1 min-h-0 flex items-center justify-center p-3 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {image ? (
          <img
            src={url}
            alt={fileName || 'پیوست'}
            className="max-h-full max-w-full object-contain rounded-lg shadow-2xl bg-white/5"
          />
        ) : pdf ? (
          <iframe
            title={fileName || 'PDF'}
            src={url}
            className="w-full h-full min-h-[70vh] rounded-lg bg-white shadow-2xl"
          />
        ) : (
          <div className="rounded-xl bg-white p-8 text-center max-w-sm space-y-4">
            <FileText size={48} className="mx-auto text-gray-400" />
            <p className="text-gray-700 text-sm">پیش‌نمایش این نوع فایل پشتیبانی نمی‌شود.</p>
            <a
              href={url}
              download={fileName || true}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm"
            >
              <Download size={16} />
              دانلود فایل
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttachmentPreviewModal;
